import { TFunction } from 'i18next';

import {
  CollectionsIcon,
  CreateLessonIcon,
  PreviewIcon,
  CalendarIcon,
  GroupIcon,
  TeacherGuideIcon,
} from 'shared/ui/icons';
import { getAmpConfig } from 'shared/config/amp-config';
import { PD_LIBRARY_ASSET } from 'shared/config/entitelment-constants';
import { hasAsset } from 'shared/lib/utils';
import { Assignment } from 'shared/api/assignment-service';
import { Asset } from 'shared/api/retail';
import { Org, PUYALLUP_SD_ORG_SOURCE_ID } from 'shared/api/onboarding';
import { AuthService } from 'shared/lib/keycloak';

type DisplayEntitlementsArgs = {
  userAssets?: Asset[];
  scheduledAssignments?: Assignment[] | null;
  isEntitledToAdminPortal?: boolean;
};

export const getQuickLinksItemsData = (
  t: TFunction<'translation', undefined>,
) => [
  {
    id: 1,
    LinkIcon: GroupIcon,
    linkName: t('Manage Classes'),
    getLinkHref() {
      return `${getAmpConfig().account}/admin-portal`;
    },
    checkDisplayEntitlements({
      isEntitledToAdminPortal,
    }: DisplayEntitlementsArgs) {
      return !!isEntitledToAdminPortal;
    },
    checkDisplayByRole() {
      return !AuthService.isReviewer();
    },
  },
  {
    id: 2,
    LinkIcon: TeacherGuideIcon,
    linkName: t('PD Library'),
    getLinkHref() {
      return getAmpConfig().pdLibrary;
    },
    checkDisplayEntitlements({ userAssets }: DisplayEntitlementsArgs) {
      if (!userAssets?.length) return false;

      return hasAsset(PD_LIBRARY_ASSET, userAssets);
    },
  },
  {
    id: 3,
    LinkIcon: CollectionsIcon,
    linkName: t('Desmos Classroom'),
    getLinkHref() {
      const desmosK5CollectionURL = new URL(getAmpConfig().desmosTeacher);

      desmosK5CollectionURL.pathname = 'collection/651ca31cf69ee59aa9e3818a';

      return desmosK5CollectionURL.href;
    },
    checkDisplayByOnboardingData: (orgs: Org[]) =>
      !orgs.some((org) => org.sourcedId === PUYALLUP_SD_ORG_SOURCE_ID),
  },
  {
    id: 4,
    LinkIcon: CreateLessonIcon,
    linkName: t('Custom Activities'),
    getLinkHref() {
      return getAmpConfig().desmosCustomActivities;
    },
    checkDisplayByOnboardingData: (orgs: Org[]) => {
      return !orgs.some((org) => org.sourcedId === PUYALLUP_SD_ORG_SOURCE_ID);
    },
  },
  {
    id: 5,
    LinkIcon: PreviewIcon,
    linkName: t('Preview Student Home'),
    getLinkHref() {
      return getAmpConfig().studentHome;
    },
  },
  {
    id: 6,
    LinkIcon: CalendarIcon,
    linkName: t('Scheduled Assignments'),
    getLinkHref() {
      // do NOT change this to full url as this will lead to full app load
      return '#/scheduled';
    },
    checkDisplayEntitlements({
      scheduledAssignments,
    }: DisplayEntitlementsArgs) {
      return !!scheduledAssignments?.length;
    },
  },
];
